// Libs
import { useMutation, type UseMutationResult } from "@tanstack/react-query";
import { compact, flatten, groupBy, intersectionBy, isNil, orderBy, uniq } from "lodash";
import moment from "moment";
import type { ProviderContext } from "notistack";
import { useAuth } from "src/hooks";
import { Mutation } from "src/types/mutation";

// Types
import type { TFunction } from "src/design-system/core/TranslationContext";
import { type Channel, ChannelCategory, ChannelSourceEnum } from "src/types/channel";
import {
    AutoRulesComparePeriodEnum,
    mapComparePeriodToRangeType,
} from "src/types/enum/AutoRulesComparePeriodEnum";
import { MarketingActionType } from "src/types/enum/MarketingActionType";
import type { IdName } from "src/types/IdName";
import { type OfferAudience, type OfferDiscount, OfferTypeEnum } from "src/types/offer";
import { OfferStatus } from "src/types/offerSlot";
import { ThirdServiceCategory } from "src/types/posCredential";
import { TriggerOperatorEnum } from "src/types/trigger";
import { TriggerMetricEnum } from "src/types/trigger/triggerMetricEnum";
import type {
    ChannelSource,
    MarketingActionFormInput,
} from "src/views/marketing-actions/marketing-actions-create-view/types/MarketingActionFormInput";
import type {
    OfferFormOutput,
    SponsoredAdFormOutput,
    TriggerStatus,
} from "src/views/marketing-actions/marketing-actions-create-view/types/MarketingActionFormOutput";

// Utils
import { formatSlotToTime } from "src/utils/hourSlots";
import { getConceptVariantFromVariantList, MarketingActionVariantEnum } from "src/utils/variants";
import {
    OfferDiscountType,
    outputFormatChannel,
    outputFormatModalValues,
    outputFormatOfferCharacteristics,
    outputFormatOfferSmartScheduler,
    outputFormatPos,
    outputFormatRecurrence,
    outputFormatService,
    outputFormatSponsoredAdCharacteristics,
    outputFormatTriggerMode,
    type OutputOptions,
} from "src/views/marketing-actions/marketing-actions-create-view/MarketingAction.formator";

// API
import { createDuo } from "src/api/duoApi";
import { createOffer } from "src/api/offerApi";
import { createSponsoredAd } from "src/api/sponsored-ads";
import { CountryEnum } from "src/types/country";
import type { Item } from "src/types/item";
import { formatDateShortSlash, formatPrice, type FormatterOptions } from "src/utils/formatters";
import type { FormattedReferenceValue } from "src/views/marketing-actions/components/form/ScenarioCard/ScenarioUtils";
import { MarketingActionErrorsModalEnum } from "src/views/sponsored-ads/routes";

export const OFFER_SELECT_PRODUCT_TK = "OFFER_SELECT_PRODUCT_TK";

//#region ENUM AND TYPES
export interface DiscountOption {
    id: string;
    name: string;
    value: number;
    typeDiscount: OfferDiscountType;
}

export enum RecurrenceFrequence {
    ALLDAY = "ALLDAY",
    ONETIME = "ONETIME",
    WEEKLYDAYS = "WEEKLYDAYS",
    SAMEDAYWEEKLY = "SAMEDAYWEEKLY",
    SPE = "SPE",
}
//#endregion

//#region CONFIG
export type RecurrenceConfig = Record<
    RecurrenceFrequence,
    {
        id: number;
        name: string;
        isWeekly: boolean;
        key: RecurrenceFrequence;
    }
>;

export const UNKNOWN_CATEGORY_NAME = "flynt-unknown";

export const recurrenceConfig = {
    ALLDAY: {
        id: 2,
        name: "Tous les jours",
        isWeekly: true,
        key: RecurrenceFrequence.ALLDAY,
    },
    ONETIME: {
        id: 1,
        name: "Une seule fois",
        isWeekly: false,
        key: RecurrenceFrequence.ONETIME,
    },
    WEEKLYDAYS: {
        id: 3,
        name: "Tous les jours de la semaine (du lundi au vendredi)",
        isWeekly: true,
        key: RecurrenceFrequence.WEEKLYDAYS,
    },
    SAMEDAYWEEKLY: {
        id: 4,
        name: "Le même jour, toutes les semaines",
        isWeekly: true,
        key: RecurrenceFrequence.SAMEDAYWEEKLY,
    },
    SPE: {
        id: 5,
        name: "Personnaliser",
        isWeekly: true,
        key: RecurrenceFrequence.SPE,
    },
};

// les clefs de isBidAutoEnabled sont des audiences, on précise celle qui ne rentrent pas dans le cas par défaut
export const optionsCharacteristics = {
    [ThirdServiceCategory.Ubereats]: {
        budget: {
            value: 5,
            min: 1,
            max: 300,
        },
        isBidAutoEnabled: { default: true },
    },
    [ThirdServiceCategory.Deliveroo]: {
        budget: {
            value: 5,
            min: 5,
            max: 300,
        },
        isBidAutoEnabled: { default: false, "1": true },
    },
    offerCharacteristic: {
        value: null,
        options: [],
        products: [],
        categoryItemList: [],
        cart: null,
        discount: null,
    },
    offer: {
        options: [],
        channels: [],
        discounts: [],
        audiences: [],
        types: [],
    },
    sponsoredAd: {
        audiences: [],
    },
};
//#endregion

//#region FORMATTERS
export const formatSmartScheduler = (
    values: MarketingActionFormInput,
    options: OutputOptions
): OfferFormOutput => {
    return {
        variants: options.variants,
        ...outputFormatPos(values, options),
        ...outputFormatRecurrence(values, recurrenceConfig),
        ...outputFormatOfferSmartScheduler(values, options),
        ...outputFormatChannel(values),
        ...outputFormatModalValues(values, options),
        ...outputFormatService(values),
        ...outputFormatTriggerMode(values),
        // Edit or duplicate fields
        originalPoss: values.originalPoss, //outputFormatOriginalPos(values),
        originalOfferId: values.originalOfferId, // not already set
    };
};

export const formatOffer = (
    values: MarketingActionFormInput,
    options: OutputOptions
): OfferFormOutput => {
    return {
        variants: options.variants,
        ...outputFormatPos(values, options),
        ...outputFormatRecurrence(values, recurrenceConfig),
        ...outputFormatOfferCharacteristics(values, options),
        ...outputFormatChannel(values),
        ...outputFormatModalValues(values, options),
        ...outputFormatService(values),
        ...outputFormatTriggerMode(values),
        // Edit or duplicate fields
        originalPoss: values.originalPoss, //outputFormatOriginalPos(values),
        originalOfferId: values.originalOfferId, // not already set
    };
};

export const formatSponsoredAd = (
    values: MarketingActionFormInput,
    options: OutputOptions
): SponsoredAdFormOutput => {
    return {
        variants: options.variants,
        ...outputFormatPos(values, options),
        ...outputFormatRecurrence(values, recurrenceConfig),
        ...outputFormatService(values),
        ...outputFormatModalValues(values, options),
        ...outputFormatSponsoredAdCharacteristics(values, options),
        ...outputFormatTriggerMode(values),
        // Edit or duplicate fields
        originalPoss: values.originalPoss,
        originalSponsoredAdId: values.originalSponsoredAdId,
    };
};

export const formatDuo = (values: MarketingActionFormInput, options: OutputOptions): any => {
    return {
        variants: options.variants,
        ...outputFormatPos(values, options),
        ...outputFormatRecurrence(values, recurrenceConfig),
        offerCharacteristics: outputFormatOfferCharacteristics(values, options),
        sponsoredAdCharacteristics: outputFormatSponsoredAdCharacteristics(values, options),
        ...outputFormatChannel(values),
        ...outputFormatService(values),
        ...outputFormatModalValues(values, options),
        ...outputFormatTriggerMode(values),
        // // Edit or duplicate fields
        originalPoss: values.originalPoss,
        originalOfferId: values.originalOfferId, // not already set
        originalSponsoredAdId: values.originalSponsoredAdId,
    };
};

//#endregion

//#region UTILS FOR SUBMIT

const getCorrectPosValue = (
    values: MarketingActionFormInput,
    posListOptions: IdName[],
    allOption: IdName
): IdName[] => {
    let posValue: IdName[] = values.posList;
    if (posValue.length === 1 && posValue[0].id === allOption.id) {
        posValue = posListOptions.map((pos) => {
            return { id: pos.id, name: pos.name };
        });
    }
    return posValue;
};
//#endregion

//#region HANDLER

const handleClickCancelDuoProg = (
    t: TFunction,
    removeVariant: (variant: MarketingActionVariantEnum) => void,
    enqueueSnackbar: ProviderContext["enqueueSnackbar"]
): void => {
    removeVariant(MarketingActionVariantEnum.Duo);
    enqueueSnackbar(t(`${MarketingActionType.Duo}.cancel-duo-alert`), {
        variant: "success",
    });
};

export const useMutationMarketingActionCreate = (
    variants: MarketingActionVariantEnum[]
): UseMutationResult<any, any> => {
    const { user } = useAuth();
    let mutationFn = handleSubmitOfferCreate;

    if (variants.includes(MarketingActionVariantEnum.Duo)) {
        mutationFn = handleSubmitDuoCreate;
    } else if (variants.includes(MarketingActionVariantEnum.SponsoredAd)) {
        mutationFn = handleSubmitSponsoredAdsCreate;
    } else if (variants.includes(MarketingActionVariantEnum.SmartScheduler)) {
        mutationFn = handleSubmitSmartSchedulerCreate;
    }

    return useMutation({
        mutationKey: [Mutation.MarketingActionCreate, user.id],
        mutationFn: (props: any) => mutationFn({ ...props, organizationId: user?.organization.id }),
    });
};

function buildGlobalErrors(key: string, options: OutputOptions): void {
    if (options?.enqueueSnackbar && options.setFieldValue) {
        options?.enqueueSnackbar(
            options.t("globalErrors", { concept: options.translateCommon(key) }),
            {
                variant: "error",
            }
        );
        options.setFieldValue("confirm", false);
    }
}

export async function handleSubmitSponsoredAdsCreate({
    values,
    options,
    organizationId,
}: {
    values: MarketingActionFormInput;
    options: OutputOptions;
    organizationId?: number;
}): Promise<void> {
    const sponsoredAd = formatSponsoredAd(values, options);
    const dataReturn = await createSponsoredAd(sponsoredAd, organizationId);

    if (!dataReturn) {
        buildGlobalErrors("concepts.sponsoredAd", options);
        return;
    }
    const {
        data: { offer },
    } = dataReturn;
    if ([OfferStatus.InCreation, OfferStatus.Scheduled].includes(offer.status)) {
        options.redirect();
        options.enqueueSnackbar(options.t("tooltip.async-create-sponsored-ad"), {
            variant: "info",
        });
    } else if (offer.status === MarketingActionErrorsModalEnum.parallelActionsDisabled) {
        buildGlobalErrors("concepts.sponsoredAd", options);
        return;
    } else {
        options.setMarketingActionErrorsModal(offer.status);
        return offer;
    }
}

export async function handleSubmitSmartSchedulerCreate({
    values,
    options,
    organizationId,
}: {
    values: MarketingActionFormInput;
    options: OutputOptions;
    organizationId?: number;
}): Promise<void> {
    const offerValues = formatSmartScheduler(values, options);

    const dataReturn = await createOffer(offerValues, organizationId);

    if (!dataReturn) {
        buildGlobalErrors("concepts.smart-scheduler", options);
        return;
    }

    const {
        data: { offer },
    } = dataReturn;

    if ([OfferStatus.InCreation, OfferStatus.Scheduled].includes(offer.status)) {
        options.redirect();
        options.enqueueSnackbar(options.t("tooltip.async-create-offer"), { variant: "info" });
    } else if (offer.status === MarketingActionErrorsModalEnum.parallelActionsDisabled) {
        buildGlobalErrors("concepts.sponsoredAd", options);
        return;
    } else {
        options.setMarketingActionErrorsModal(offer.status);
        return offer;
    }
}

export async function handleSubmitOfferCreate({
    values,
    options,
    organizationId,
}: {
    values: MarketingActionFormInput;
    options: OutputOptions;
    organizationId?: number;
}): Promise<void> {
    const offerValues = formatOffer(values, options);
    const dataReturn = await createOffer(offerValues, organizationId);

    if (!dataReturn) {
        buildGlobalErrors("concepts.offer", options);
        return;
    }

    const {
        data: { offer },
    } = dataReturn;

    if ([OfferStatus.InCreation, OfferStatus.Scheduled].includes(offer.status)) {
        options.redirect();
        options.enqueueSnackbar(options.t("tooltip.async-create-offer"), { variant: "info" });
    } else if (offer.status === MarketingActionErrorsModalEnum.parallelActionsDisabled) {
        buildGlobalErrors("concepts.sponsoredAd", options);
        return;
    } else {
        options.setMarketingActionErrorsModal(offer.status);
        return offer;
    }
}

export async function handleSubmitDuoCreate({
    values,
    options,
    organizationId,
}: {
    values: MarketingActionFormInput;
    options: OutputOptions;
    organizationId?: number;
}): Promise<any> {
    const duoValues = formatDuo(values, options);
    const dataReturn = await createDuo(duoValues, organizationId);
    if (!dataReturn) {
        buildGlobalErrors("concepts.duo", options);
        return;
    }
    const {
        data: { offer },
    } = dataReturn;

    if (
        [OfferStatus.InCreation, OfferStatus.Scheduled].includes(offer?.offer?.status) &&
        [OfferStatus.InCreation, OfferStatus.Scheduled].includes(offer?.sponsoredAd?.status)
    ) {
        options.redirect();
        options.enqueueSnackbar(options.t("tooltip.async-create-duo"), { variant: "info" });
    } else if (offer.status === MarketingActionErrorsModalEnum.parallelActionsDisabled) {
        buildGlobalErrors("concepts.sponsoredAd", options);
        return;
    } else {
        options.setMarketingActionErrorsModal(offer.status);
        return offer;
    }
}

//#endregion

//#region CONFIG BUILDER
/**
 * Expliquer le formator en entrée
 * Expliquer le formator en sortie
 * le build options
 * et l’initial state
 */

export const checkIfSpecialDiscountFound = (offerAudience: OfferAudience): boolean => {
    return offerAudience?.categoryDiscounts?.some((categoryDiscount) =>
        categoryDiscount?.discounts?.some(
            (discount) => discount.isEnabled === true && discount.isSpecialDiscount === true
        )
    );
};

export const buildBaseCurrentKey = (
    discount: Pick<OfferDiscount, "type" | "percentDiscount" | "flatDiscount">
): string => {
    let discountValue: number | null = null;
    let typeDiscount: OfferDiscountType | null = null;
    if (discount.percentDiscount) {
        typeDiscount = OfferDiscountType.PercentDiscount;
        discountValue = discount.percentDiscount;
    } else if (discount.flatDiscount) {
        typeDiscount = OfferDiscountType.FlatDiscount;
        discountValue = discount.flatDiscount;
    }
    return compact([discount.type, typeDiscount, discountValue]).join("-");
};

export const buildCurrentKey = (title: string, subTitle: string, startDate: string): string => {
    return `${title}-${subTitle}-${startDate}`;
};

type buildOptionsParams = {
    channelSources: ChannelSource[];
    channelsData: Channel[];
    t: TFunction;
    endTime: number;
    startTime: number;
    isSpecial: boolean;
    triggerStartStatus: TriggerStatus;
    variants: MarketingActionVariantEnum[];
    audienceId: number;
    formatOptions: FormatterOptions;
};

type buildOptionsOutput = {
    sources: {
        options: {
            [K in ChannelSourceEnum]?: {
                name: string;
                offerAudiences: IdName[];
                sponsoredAdAudiences: IdName[];
                sponsoredAdAutoBiddingPosIds: number[];
                isSpecialDiscountFound: boolean;
                condition: {
                    triggerFieldOption: IdName[];
                    triggerReferencePeriodFieldOption: IdName[];
                    triggerStopStatusFieldOption: IdName[];
                    triggerStartStatusFieldOption: IdName[];
                    triggerReferenceChannelOption: IdName[];
                    triggerOperatorFieldOption: IdName[];
                };
                categoryDiscounts: {
                    values: IdName[];
                    options: {
                        [K in ChannelCategory]?: {
                            maxItems: { [K in OfferTypeEnum]?: number };
                            type: OfferTypeEnum[];
                            /** String is a combination of `${discount.type}-${flat/percent discount}` */
                            cart: Record<string, IdName[]>;
                            discount: {
                                [K in OfferTypeEnum]?: IdName;
                            };
                            specialOperation: (Pick<
                                OfferDiscount,
                                | "specialOperationSubTitle"
                                | "specialOperationStartDate"
                                | "specialOperationEndDate"
                                | "specialOperationStartTime"
                                | "specialOperationEndTime"
                            > & {
                                id: number;
                                name: string;
                                flatDiscount: OfferDiscount | null;
                                percentDiscount: OfferDiscount | null;
                                minBasket: number | null;
                                type: OfferTypeEnum;
                                isMultipleDiscount: boolean;
                            })[];
                        };
                    };
                };
            };
        };
        values: IdName[];
    };
};

const buildOptions = ({
    channelSources,
    channelsData,
    t,
    endTime,
    startTime,
    isSpecial,
    triggerStartStatus,
    variants,
    audienceId,
    formatOptions,
}: buildOptionsParams): buildOptionsOutput => {
    const concept = getConceptVariantFromVariantList(variants);
    const { translateCommon } = formatOptions;

    return {
        sources: {
            options: {
                ...channelSources
                    .filter((e) => channelsData.map((el) => el.source).includes(e.source))
                    .reduce((acc, elm) => {
                        return {
                            ...acc,
                            [elm.source]: {
                                name: elm.source,
                                duoAudiences: intersectionBy(
                                    elm.offerAudiences,
                                    elm.sponsoredAdAudiences,
                                    (a: IdName) => a.id
                                ),
                                offerAudiences: elm.offerAudiences,
                                sponsoredAdAudiences: elm.sponsoredAdAudiences,
                                sponsoredAdAutoBiddingPosIds: elm.sponsoredAdAutoBiddingPosIds,
                                isSpecialDiscountFound: checkIfSpecialDiscountFound(
                                    elm.offerAudiences.find((e) => e.id === audienceId)
                                ),
                                condition: {
                                    triggerFieldOption: [
                                        {
                                            id: TriggerMetricEnum.DailySales,
                                            name: t(
                                                `${TriggerMetricEnum.DailySales}.auto-rule-trigger-reason`
                                            ),
                                        },
                                        {
                                            id: TriggerMetricEnum.DailySalesExvat,
                                            name: t(
                                                `${TriggerMetricEnum.DailySalesExvat}.auto-rule-trigger-reason`
                                            ),
                                        },
                                        {
                                            id: TriggerMetricEnum.DailyOrders,
                                            name: t(
                                                `${TriggerMetricEnum.DailyOrders}.auto-rule-trigger-reason`
                                            ),
                                        },
                                    ],
                                    triggerReferencePeriodFieldOption: [
                                        {
                                            id: AutoRulesComparePeriodEnum.lastWeek,
                                            name: translateCommon(
                                                `enums.compare-time-range.${mapComparePeriodToRangeType(
                                                    AutoRulesComparePeriodEnum.lastWeek
                                                )}`
                                            ),
                                        },
                                        {
                                            id: AutoRulesComparePeriodEnum.lastTwoWeeks,
                                            name: translateCommon(
                                                `enums.compare-time-range.${mapComparePeriodToRangeType(
                                                    AutoRulesComparePeriodEnum.lastTwoWeeks
                                                )}`
                                            ),
                                        },
                                        {
                                            id: AutoRulesComparePeriodEnum.lastFourWeeks,
                                            name: translateCommon(
                                                `enums.compare-time-range.${mapComparePeriodToRangeType(
                                                    AutoRulesComparePeriodEnum.lastFourWeeks
                                                )}`,
                                                {
                                                    displayAverage: "",
                                                }
                                            ),
                                        },
                                        {
                                            id: AutoRulesComparePeriodEnum.weekLastYear,
                                            name: translateCommon(
                                                `enums.compare-time-range.${mapComparePeriodToRangeType(
                                                    AutoRulesComparePeriodEnum.weekLastYear
                                                )}`
                                            ),
                                        },
                                        {
                                            id: AutoRulesComparePeriodEnum.monthLastYear,
                                            name: translateCommon(
                                                `enums.compare-time-range.${mapComparePeriodToRangeType(
                                                    AutoRulesComparePeriodEnum.monthLastYear
                                                )}`
                                            ),
                                        },
                                    ],
                                    triggerStopStatusFieldOption: [
                                        {
                                            id: "disable",
                                            name: t(`${concept}.auto-rule-radio-one`, {
                                                hour: endTime
                                                    ? formatSlotToTime(endTime, translateCommon)
                                                    : "-",
                                            }),
                                            disabled: triggerStartStatus === "disable",
                                            disabledReason:
                                                triggerStartStatus === "disable"
                                                    ? t(`auto-rule-radio-one-disable-reason`)
                                                    : "",
                                        },
                                        { id: "enable", name: t(`${concept}.auto-rule-radio-two`) },
                                    ],
                                    triggerStartStatusFieldOption: [
                                        {
                                            id: "disable",
                                            name: t(`${concept}.auto-rule-start-radio-two`, {
                                                hour: startTime
                                                    ? formatSlotToTime(startTime, translateCommon)
                                                    : "-",
                                            }),
                                        },
                                        { id: "enable", name: t(`auto-rule-start-radio-one`) },
                                    ],
                                    triggerReferenceChannelOption: channelsData
                                        ? channelsData.map((channel) => {
                                              return {
                                                  id: channel.id,
                                                  name: translateCommon(`channel.${channel.name}`),
                                              };
                                          })
                                        : [],
                                    triggerOperatorFieldOption: [
                                        {
                                            id: TriggerOperatorEnum.LessThan,
                                            name: t(`auto-rule-less-than-one`),
                                        },
                                        {
                                            id: TriggerOperatorEnum.MoreThan,
                                            name: t(`auto-rule-more-than-one-short`),
                                        },
                                    ],
                                },
                                categoryDiscounts: {
                                    values:
                                        elm.source === ChannelSourceEnum.Ubereats ||
                                        elm.source === ChannelSourceEnum.JustEat
                                            ? [
                                                  {
                                                      id: ChannelCategory.Delivery,
                                                      name: translateCommon(
                                                          `enums.mode.takeaway-delivery`
                                                      ),
                                                  },
                                              ]
                                            : // trick crado a cause de l'audience ID dynamique il faut corriger ca asap pour retirer ce code horrible. Désolé :(
                                              elm.offerAudiences?.find(
                                                    (oad) => oad.id === audienceId
                                                ) !== undefined
                                              ? elm.offerAudiences
                                                    ?.find((oad) => oad.id === audienceId)
                                                    ?.categoryDiscounts?.map((cat) => {
                                                        return {
                                                            id: cat.category,
                                                            name: translateCommon(
                                                                `enums.mode.${cat.category}`
                                                            ),
                                                        };
                                                    })
                                              : elm.offerAudiences
                                                    ?.find((oad) => oad.id === 1)
                                                    ?.categoryDiscounts?.map((cat) => {
                                                        return {
                                                            id: cat.category,
                                                            name: translateCommon(
                                                                `enums.mode.${cat.category}`
                                                            ),
                                                        };
                                                    }),
                                    options: {
                                        ...elm.offerAudiences
                                            .find((oad) => oad.id === audienceId)
                                            ?.categoryDiscounts?.reduce((accCategory, elm) => {
                                                const elmCat = {
                                                    ...elm,
                                                    discounts: elm?.discounts?.filter((e) => {
                                                        if (e.isEnabled === false) {
                                                            return false;
                                                        }
                                                        return e.isSpecialDiscount === isSpecial;
                                                    }),
                                                };

                                                return {
                                                    ...accCategory,
                                                    [elmCat.category]: {
                                                        maxItems: {
                                                            ...Object.entries(
                                                                groupBy(
                                                                    elmCat.discounts,
                                                                    (t) => t.type
                                                                )
                                                            ).reduce(
                                                                (
                                                                    otherAcc,
                                                                    [key, valuesDiscount]
                                                                ) => {
                                                                    return {
                                                                        ...otherAcc,
                                                                        [key]: valuesDiscount[0]
                                                                            .maxItem,
                                                                    };
                                                                },
                                                                {}
                                                            ),
                                                        },
                                                        type: Object.values(OfferTypeEnum).filter(
                                                            (offerType) =>
                                                                Object.keys(
                                                                    groupBy(
                                                                        elmCat.discounts,
                                                                        (t) => t.type
                                                                    )
                                                                ).includes(offerType)
                                                        ),
                                                        cart: {
                                                            ...elmCat.discounts.reduce(
                                                                (otherAcc, discount) => {
                                                                    let currentKey =
                                                                        buildBaseCurrentKey(
                                                                            discount
                                                                        );

                                                                    if (
                                                                        isSpecial === false &&
                                                                        discount.minBasket === null
                                                                    ) {
                                                                        return otherAcc;
                                                                    }

                                                                    if (isSpecial) {
                                                                        currentKey = `${currentKey}-${buildCurrentKey(
                                                                            discount.specialOperationTitle,
                                                                            discount.specialOperationSubTitle,
                                                                            discount.specialOperationStartDate
                                                                        )}`;
                                                                    }

                                                                    const returnValues = {
                                                                        ...otherAcc,
                                                                        [currentKey]: {
                                                                            values: [
                                                                                ...(otherAcc[
                                                                                    currentKey
                                                                                ]?.values || []),
                                                                                {
                                                                                    id: discount.minBasket,
                                                                                    name: formatPrice(
                                                                                        +discount.minBasket,
                                                                                        formatOptions
                                                                                    ),
                                                                                },
                                                                            ],
                                                                        },
                                                                    };

                                                                    return returnValues;
                                                                },
                                                                {}
                                                            ),
                                                        },
                                                        discount: {
                                                            ...Object.entries(
                                                                groupBy(elmCat.discounts, (t) => {
                                                                    if (isSpecial) {
                                                                        return `${t.type}-${buildCurrentKey(
                                                                            t.specialOperationTitle,
                                                                            t.specialOperationSubTitle,
                                                                            t.specialOperationStartDate
                                                                        )}`;
                                                                    }
                                                                    return t.type;
                                                                })
                                                            ).reduce(
                                                                (
                                                                    otherAcc,
                                                                    [key, valuesDiscountOptions]
                                                                ) => {
                                                                    const valuesDiscount = orderBy(
                                                                        valuesDiscountOptions.filter(
                                                                            (elm) =>
                                                                                elm.percentDiscount ||
                                                                                elm.flatDiscount
                                                                        ),
                                                                        // We want percent discount group first
                                                                        (dis) =>
                                                                            dis.percentDiscount
                                                                                ? 1
                                                                                : 2
                                                                    );

                                                                    return {
                                                                        ...otherAcc,
                                                                        [key]: valuesDiscount.reduce(
                                                                            (
                                                                                accValueDiscount,
                                                                                vDis
                                                                            ) => {
                                                                                const typeDiscount =
                                                                                    vDis.percentDiscount
                                                                                        ? OfferDiscountType.PercentDiscount
                                                                                        : OfferDiscountType.FlatDiscount;
                                                                                const formatter =
                                                                                    typeDiscount ===
                                                                                    OfferDiscountType.PercentDiscount
                                                                                        ? (
                                                                                              p: number
                                                                                          ) =>
                                                                                              `${p / 100} %`
                                                                                        : (
                                                                                              p: number
                                                                                          ) =>
                                                                                              formatPrice(
                                                                                                  p,
                                                                                                  formatOptions
                                                                                              );
                                                                                const val =
                                                                                    vDis.percentDiscount ||
                                                                                    vDis.flatDiscount;

                                                                                const formattedVDis =
                                                                                    {
                                                                                        id: buildBaseCurrentKey(
                                                                                            vDis
                                                                                        ),
                                                                                        name: formatter(
                                                                                            +val
                                                                                        ),
                                                                                        value: val,
                                                                                        typeDiscount:
                                                                                            typeDiscount,
                                                                                    };
                                                                                const isAlreadyInAcc =
                                                                                    accValueDiscount.some(
                                                                                        (accVDis) =>
                                                                                            accVDis.name ===
                                                                                                formattedVDis.name &&
                                                                                            accVDis.typeDiscount ===
                                                                                                formattedVDis.typeDiscount
                                                                                    );
                                                                                if (
                                                                                    !isNil(val) &&
                                                                                    !isAlreadyInAcc
                                                                                ) {
                                                                                    accValueDiscount.push(
                                                                                        formattedVDis
                                                                                    );
                                                                                }

                                                                                return accValueDiscount;
                                                                            },
                                                                            []
                                                                        ),
                                                                    };
                                                                },
                                                                {}
                                                            ),
                                                        },
                                                        specialOperation: elmCat.discounts
                                                            .filter((special) => {
                                                                return (
                                                                    special.specialOperationTitle &&
                                                                    special.specialOperationSubTitle &&
                                                                    special.specialOperationStartDate &&
                                                                    special.specialOperationEndDate
                                                                );
                                                            })
                                                            .sort((a, b) => {
                                                                return (
                                                                    new Date(
                                                                        a.specialOperationStartDate
                                                                    ).getTime() -
                                                                    new Date(
                                                                        b.specialOperationStartDate
                                                                    ).getTime()
                                                                );
                                                            })
                                                            .reduce((acc, item, _, allValues) => {
                                                                if (
                                                                    acc.find(
                                                                        (elm) =>
                                                                            elm.specialOperationSubTitle ===
                                                                                item.specialOperationSubTitle &&
                                                                            elm.specialOperationStartDate ===
                                                                                item.specialOperationStartDate
                                                                    )
                                                                ) {
                                                                    return acc;
                                                                }

                                                                const isMultipleDiscount =
                                                                    allValues.find(
                                                                        (elm) =>
                                                                            elm.specialOperationSubTitle ===
                                                                                item.specialOperationSubTitle &&
                                                                            elm.id !== item.id &&
                                                                            elm.specialOperationStartDate ===
                                                                                item.specialOperationStartDate
                                                                    );
                                                                acc.push({
                                                                    id: item.id,
                                                                    audienceId: item.audienceId,
                                                                    currentKey: buildCurrentKey(
                                                                        item.specialOperationTitle,
                                                                        item.specialOperationSubTitle,
                                                                        item.specialOperationStartDate
                                                                    ),
                                                                    name:
                                                                        `${translateCommon(`word-link.week`)}-` +
                                                                        moment(
                                                                            item.specialOperationStartDate
                                                                        ).format("w") +
                                                                        " - " +
                                                                        item.specialOperationTitle +
                                                                        ` - ${translateCommon(`word-link.from`)} ` +
                                                                        moment(
                                                                            item.specialOperationStartDate
                                                                        ).format(
                                                                            formatDateShortSlash()
                                                                        ) +
                                                                        ` - ${translateCommon(`word-link.to`)} ` +
                                                                        moment(
                                                                            item.specialOperationEndDate
                                                                        ).format(
                                                                            formatDateShortSlash()
                                                                        ) +
                                                                        " - " +
                                                                        item.specialOperationSubTitle,
                                                                    flatDiscount: isMultipleDiscount
                                                                        ? null
                                                                        : item.flatDiscount,
                                                                    specialOperationSubTitle:
                                                                        item.specialOperationSubTitle,
                                                                    specialOperationStartDate:
                                                                        item.specialOperationStartDate,
                                                                    specialOperationEndDate:
                                                                        item.specialOperationEndDate,
                                                                    specialOperationStartTime:
                                                                        item.specialOperationStartTime,
                                                                    specialOperationEndTime:
                                                                        item.specialOperationEndTime,
                                                                    percentDiscount:
                                                                        isMultipleDiscount
                                                                            ? null
                                                                            : item.percentDiscount,
                                                                    minBasket: isMultipleDiscount
                                                                        ? null
                                                                        : item.minBasket,
                                                                    type: item.type,
                                                                    isMultipleDiscount,
                                                                });
                                                                return acc;
                                                            }, []),
                                                    },
                                                };
                                            }, {}),
                                    },
                                },
                            },
                        };
                    }, {}),
            },
            values: channelSources
                // filter uniquement les plateforme accessible par les POS sélectionné dans la top bar
                .filter((e) => channelsData.map((el) => el.source).includes(e.source))
                // filter uniquement Uber & DLV pour les opération spéciales et les DUO
                .filter((channelSource) => {
                    if (
                        variants.includes(MarketingActionVariantEnum.Special) ||
                        variants.includes(MarketingActionVariantEnum.Duo)
                    ) {
                        return [ChannelSourceEnum.Ubereats, ChannelSourceEnum.Deliveroo].includes(
                            channelSource.source
                        );
                    } else {
                        return channelSource;
                    }
                })
                .reduce((acc, elm) => {
                    const needOfferAccess =
                        variants.includes(MarketingActionVariantEnum.Offer) ||
                        variants.includes(MarketingActionVariantEnum.Duo);
                    const needSponsoredAdAccess =
                        variants.includes(MarketingActionVariantEnum.SponsoredAd) ||
                        variants.includes(MarketingActionVariantEnum.Duo);

                    if (
                        (needOfferAccess &&
                            elm.authorizedMarketingAction.includes(MarketingActionType.Offer)) ||
                        (needSponsoredAdAccess &&
                            elm.authorizedMarketingAction.includes(MarketingActionType.SponsoredAd))
                    ) {
                        acc.push({
                            id: elm.source,
                            name: translateCommon(`enums.channel-source.${elm.source}`),
                        });
                    }

                    return acc;
                }, []),
        },
    };
};
//#endregion

function getCurrentProductList(oldProductList, availableProductList: Item[]) {
    return oldProductList
        ?.reduce((acc, product) => {
            const itm = availableProductList.find(
                (ni) =>
                    ni.name === product.name &&
                    (ni?.remoteCategory?.name ?? UNKNOWN_CATEGORY_NAME) ===
                        (product?.categoryName ??
                            product?.remoteCategory?.name ??
                            UNKNOWN_CATEGORY_NAME)
            );
            if (itm) {
                acc.push({
                    ...itm,
                    name: itm.name,
                    discountOption: +product.discountOption,
                    remoteCategory: {
                        name: itm.remoteCategory?.name,
                    },
                    id: `${itm.name}-${itm.remoteCategory?.name}`,
                });
            }
            return acc;
        }, [])
        .flat();
}

function getCurrentCategoryList(oldCategoryList, availableProductList: Item[]) {
    return oldCategoryList
        ?.reduce((acc, category) => {
            const filterItemList = availableProductList.filter((ni) => {
                const compareCategory =
                    category?.categoryName ??
                    category?.remoteCategory?.name ??
                    UNKNOWN_CATEGORY_NAME;
                return (
                    category?.itemList?.map((elm) => elm.name).includes(ni.name) &&
                    (ni?.remoteCategory?.name ?? UNKNOWN_CATEGORY_NAME) === compareCategory
                );
            });
            if (filterItemList) {
                const currentPosList = uniq(flatten(filterItemList.map((itm) => itm.posIds)));
                if (currentPosList.length) {
                    acc.push({
                        ...category,
                        posIds: currentPosList,
                    });
                }
            }

            return acc;
        }, [])
        .flat();
}

// C'est la combinaison des 2 qui doit définir si oui ou non on est dans le cas de la selection par categories
export const SERVICE_ALLOW_CATEGORIES_SELECTION = [];
export const OFFER_TYPE_ALLOW_CATEGORIES_SELECTION = [OfferTypeEnum.Bogo];
export const OFFER_TYPE_DISABLED_CHEECKY_SELECTION = [OfferTypeEnum.FlatOff];
export const CHEECKY_TUESDAY_COUNTRIES = [CountryEnum.GB];
export const CHEECKY_TUESDAY_DAY = 2;

function isDayInRange(
    startDate: moment.Moment,
    endDate: moment.Moment | null,
    targetDay: number
): boolean {
    if (!startDate.isValid() || startDate.isAfter(endDate)) {
        return false;
    }

    if (isNil(endDate) === true || endDate.isValid() === false) {
        // Utilisé pour les cas d'une offre ponctuelle sur 1 seul jour
        return startDate.day() === targetDay;
    }

    const currentDate = startDate.clone();
    while (currentDate.isSameOrBefore(endDate)) {
        if (currentDate.day() === targetDay) {
            return true;
        }
        currentDate.add(1, "day");
    }

    return false;
}

export function isAllowCategorySelectionByServiceAndOfferType(
    service: ThirdServiceCategory,
    offerType: OfferTypeEnum
): boolean {
    return (
        SERVICE_ALLOW_CATEGORIES_SELECTION.includes(service) &&
        OFFER_TYPE_ALLOW_CATEGORIES_SELECTION.includes(offerType)
    );
}

export function isCheeckyTuesday({
    service,
    offerType,
    startDate,
    endDate,
    startTime,
    endTime,
    days,
    isNonFlynt,
    countryCode,
}: {
    service: ThirdServiceCategory;
    offerType: OfferTypeEnum;
    startDate: moment.Moment;
    endDate: moment.Moment;
    startTime: number;
    endTime: number;
    days: number[];
    isNonFlynt: boolean;
    countryCode?: CountryEnum;
}): boolean {
    // Bypass complet de la vérification du cheeky tuesday pour les offres hors flynt
    if (isNonFlynt === true) {
        return false;
    }
    if (SERVICE_ALLOW_CATEGORIES_SELECTION.includes(service) === false) {
        return false;
    }
    if (CHEECKY_TUESDAY_COUNTRIES.includes(countryCode) === false) {
        return false;
    }

    if (OFFER_TYPE_DISABLED_CHEECKY_SELECTION.includes(offerType) === false) {
        return false;
    }

    // TEST POUR SAVOIR SI ON EST PAS UN LUNDI MINUIT PASSE => DONC MARDI ;)
    if (
        days?.length === 0 &&
        startDate.day() === CHEECKY_TUESDAY_DAY - 1 &&
        (startTime >= 24 || endTime >= 24)
    ) {
        // CAS POUR LES OFFRES PONCTUELLES
        return true;
    } else if (
        days?.length > 0 &&
        days.includes(CHEECKY_TUESDAY_DAY - 1) === true &&
        (startTime >= 24 || endTime > 24)
    ) {
        // CAS POUR LES OFFRES RECURRENTES
        return true;
    }

    if (days.length > 0 && days.includes(CHEECKY_TUESDAY_DAY) === false) {
        return false;
    }

    if (days.length > 0 && days.includes(CHEECKY_TUESDAY_DAY) === true) {
        return true;
    }

    return isDayInRange(startDate, endDate, CHEECKY_TUESDAY_DAY);
}

/**
 * Compare specific to reference objectives values and check whether the objectives have been adjusted or not.
 * @returns True if objectives have been adjusted, false otherwise.
 */
export function checkIfObjectivesHaveBeenAdjusted(
    initialValues: FormattedReferenceValue[],
    specificValues: FormattedReferenceValue[]
): boolean {
    // check if thresholdFactor has been adjusted for any pos
    let isAdjusted =
        specificValues.find((val) => !!val.thresholdFactor && val.thresholdFactor !== 0) !==
        undefined;
    if (isAdjusted) {
        return true;
    }

    const excludedKeys: (keyof FormattedReferenceValue)[] = [
        "pos",
        "posId",
        "metric",
        "maximum",
        "isEditedDowNumberListLunch",
        "isEditedDowNumberListDinner",
        "thresholdFactor",
    ];

    // check for each day and service time of each pos
    isAdjusted = specificValues.some((specificValue) => {
        const referenceValue = initialValues.find(
            (refValue) => refValue.posId === specificValue.posId
        );
        if (!referenceValue) {
            return false;
        }

        return (
            Object.keys(specificValue)
                // keep only properties in the form {dow}_{serviceTime}
                .filter((key) => !excludedKeys.includes(key as keyof FormattedReferenceValue))
                .some((key) => specificValue[key] !== referenceValue[key])
        );
    });

    return isAdjusted;
}

//#region EXPORTS
export {
    buildOptions,
    getCorrectPosValue,
    getCurrentCategoryList,
    getCurrentProductList,
    handleClickCancelDuoProg,
};

//#endregion
